import { supabase } from "@/integrations/supabase/client";
import { MediaItem } from "@/types/media.types";

export interface UnsplashPhoto {
  id: string;
  description: string | null;
  width: number;
  height: number;
  color: string | null;
  url: string;
  thumbUrl: string;
  htmlLink: string;
  /** `links.download_location` — da pingare alla selezione (vedi trackUnsplashDownload). */
  downloadLocation: string | null;
  author: {
    name: string;
    username: string;
    link: string;
  };
}

export type UnsplashOrientation = 'landscape' | 'portrait' | 'squarish';

export interface SearchUnsplashPhotosParams {
  query: string;
  page?: number;
  per_page?: number;
  orientation?: UnsplashOrientation;
}

export interface SearchUnsplashPhotosResponse {
  results: UnsplashPhoto[];
  total: number;
  total_pages: number;
}

/**
 * Ricerca foto su Unsplash tramite l'edge function search-unsplash-photos.
 */
export const searchUnsplashPhotos = async (
  params: SearchUnsplashPhotosParams,
): Promise<SearchUnsplashPhotosResponse> => {
  const { data, error } = await supabase.functions.invoke("search-unsplash-photos", {
    body: params,
  });

  if (error) throw error;
  return data as SearchUnsplashPhotosResponse;
};

export interface GetRandomUnsplashPhotoParams {
  query?: string;
  orientation?: UnsplashOrientation;
}

/**
 * Recupera una foto casuale da Unsplash tramite l'edge function random-unsplash-photo.
 * Usata per generare il placeholder cover quando si crea un nuovo itinerario.
 */
export const getRandomUnsplashPhoto = async (
  params: GetRandomUnsplashPhotoParams = {},
): Promise<UnsplashPhoto> => {
  const { data, error } = await supabase.functions.invoke("random-unsplash-photo", {
    body: params,
  });

  if (error) throw error;
  return (data as { photo: UnsplashPhoto }).photo;
};

/**
 * Notifica a Unsplash che la foto è stata "usata" dall'utente.
 *
 * Le Unsplash API Guidelines lo richiedono a ogni selezione effettiva (non alla
 * semplice visualizzazione dei risultati di ricerca): alimenta il contatore di
 * download del fotografo. Non scarica nulla e non blocca il flusso — se fallisce
 * logghiamo e basta, l'immagine è comunque stata scelta.
 */
export const trackUnsplashDownload = async (downloadLocation?: string | null): Promise<void> => {
  if (!downloadLocation) return;

  try {
    const { error } = await supabase.functions.invoke("unsplash-track-download", {
      body: { download_location: downloadLocation },
    });
    if (error) console.warn("Unsplash download tracking failed:", error);
  } catch (err) {
    console.warn("Unsplash download tracking failed:", err);
  }
};

/**
 * Converte una foto Unsplash nel MediaItem persistito, conservando i dati di
 * attribuzione. Funzione pura: il tracking del download va notificato a parte
 * (trackUnsplashDownload) al momento della selezione, per non rischiare di
 * pingare Unsplash a ogni conversione — es. dentro un .map() di rendering.
 */
export const unsplashPhotoToMediaItem = (photo: UnsplashPhoto): MediaItem => {
  return {
    url: photo.url,
    provider: 'unsplash',
    alt: photo.description,
    unsplash: {
      photoId: photo.id,
      photoUrl: photo.htmlLink,
      photographerName: photo.author.name,
      photographerUrl: photo.author.link,
      downloadLocation: photo.downloadLocation ?? undefined,
    },
  };
};
